import type { Transaction } from "../app/services/transactions";
import type { MeBalanceSource } from "./balance";
import { formatYenSigned } from "./money";
import { addConsumptionTax } from "./tax";

const toNumber = (v: string | number | null | undefined): number => {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
};

/**
 * 取引行に表示する税込・符号付き金額ラベルを返す。
 *
 * 優先順位:
 *   1. ``display_amount``（BE 計算済みの収支方向付き税抜金額）
 *   2. ``contract_amount``（プラス表示。受託前で direction が未確定でも対価が
 *      見えるようにする）
 * 値が無い・有限でないときは ``null`` を返し、呼び出し側で非表示にする。
 *
 * 進行中取引（``/api/me/transactions``）・振込申請（``/api/me/withdrawable-
 * transactions``）の両 API で BE が ``direction`` / ``display_amount`` を
 * 付与しているため、本ユーティリティは両モーダルで共通利用できる。
 */
export const amountLabelForTransaction = (
  tx: Transaction,
): string | null => {
  const exclusive =
    tx.display_amount != null ? tx.display_amount : tx.contract_amount;
  if (exclusive == null || !Number.isFinite(Number(exclusive))) return null;
  return formatYenSigned(addConsumptionTax(Number(exclusive)));
};

/**
 * 進行中取引の差し引き合計（受託 − 委託）を税抜の数値で計算する。
 *
 * ``direction`` が ``'incoming'`` / ``'outgoing'`` に分かれているため、
 * ``display_amount`` をそのまま合算するだけで「受託分 − 委託分」になる。
 * ``display_amount`` が未設定（draft 等）の行は 0 として扱う。
 */
export const computeTransactionsNetExclusive = (
  transactions: ReadonlyArray<Transaction>,
): number => {
  let total = 0;
  for (const tx of transactions) {
    const amount = tx.display_amount;
    if (amount == null || !Number.isFinite(Number(amount))) continue;
    total += Number(amount);
  }
  return total;
};

/**
 * 進行中モーダル上部サマリの内訳と合計（すべて税込・整数円）。
 *
 * UI では「オファーに使える残高」を主役にし、その内訳として
 * 「残高 / チャージ済み / 入金予定 / 支払予定」の 4 行を並べる。
 * **4 行の符号付き合計が ``availableInclusive`` と一致**するように設計
 * しており、ユーザーは内訳と合計の関係を直感的に把握できる。
 *
 *     残高（settled）+ チャージ済み（charged）+ 入金予定（incoming）
 *       − 支払予定（outgoing） = オファーに使える残高（available）
 *
 * フィールド名は UI ラベルに対応する（``settled`` / ``charged`` /
 * ``incoming`` / ``outgoing`` / ``available``）。すべて税込・正値で、
 * ``outgoing`` のみ表示時にマイナス符号を付ける。
 */
export interface TransactionsBalanceSummary {
  /** 残高（=``balance_settled``）。仕事完了後に振込申請できる残高。 */
  settledInclusive: number;
  /** チャージ済み（=``balance_free + balance_committed_outgoing``）。
   *  支払予定で拘束される前の総入金額。``outgoingInclusive`` 行と
   *  打ち消し合うことで 4 行合計が ``availableInclusive`` になる。 */
  chargedInclusive: number;
  /** 入金予定（=``balance_committed_incoming``）。受託中オファーの完了後に
   *  入金される額。新仕様ではオファーの原資としても利用可能。 */
  incomingInclusive: number;
  /** 支払予定（=``balance_committed_outgoing``）。委託中オファーで拘束中の
   *  金額。表示時はマイナス符号を付ける。 */
  outgoingInclusive: number;
  /** オファーに使える残高。BE ``escrow_lock`` の利用可能額
   *  （``free + settled + committed_incoming``）と一致する。 */
  availableInclusive: number;
}

/**
 * ``/api/me`` の ``balance`` から進行中モーダル用の税込金額を計算する。
 *
 * ``balance`` が無い／未取得のときは全項目 0 を返す。BE は各値を文字列で返す
 * ことがあるため、数値変換は本関数内で実施する。
 */
export const computeTransactionsBalanceSummary = (
  userData: MeBalanceSource | null | undefined,
): TransactionsBalanceSummary => {
  const balance = userData?.balance ?? {};
  const free = toNumber(balance?.free);
  const settled = toNumber(balance?.settled);
  const committedIncoming = toNumber(balance?.committed_incoming);
  const committedOutgoing = toNumber(balance?.committed_outgoing);

  return {
    settledInclusive: addConsumptionTax(settled),
    chargedInclusive: addConsumptionTax(free + committedOutgoing),
    incomingInclusive: addConsumptionTax(committedIncoming),
    outgoingInclusive: addConsumptionTax(committedOutgoing),
    availableInclusive: addConsumptionTax(
      free + settled + committedIncoming,
    ),
  };
};
